import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useQuery, UseQueryResult } from "react-query";
import { useOrg } from "src/hooks/useOrg";

export type EntityWorkContext = {
  orgId: string;
  entityUuid?: string;
  actorUuid?: string;
  types?: string[];
  search?: string;
  pagingSize?: number;
  query: UseQueryResult<any>;
  dataRef: React.MutableRefObject<any>;
  setPagination: React.Dispatch<
    React.SetStateAction<{
      page: number;
      pageSize: number;
    }>
  >;
};

const PAGESIZE = 10;

export function useEntityWork({
  orgId,
  entityUuid,
  actorUuid,
  types,
  search,
  pagingSize,
}: Omit<
  EntityWorkContext,
  "query" | "setPagination" | "dataRef"
>): EntityWorkContext {
  orgId = useOrg().id!; // sometimes orgId is taken from an entitie details properties - which is wrong (and should/will be undefined). Couldn't figure out where exactly that is done.
  const [{ page, pageSize }, setPagination] = useState({
    page: 1,
    pageSize: pagingSize || PAGESIZE,
  });
  useEffect(() => setPagination({ page, pageSize }), [page, pageSize]);
  const query = useQuery(
    [
      "entityWork",
      { orgId, entityUuid, actorUuid, types, search, page, pageSize },
    ],
    async () =>
      fetch(`/api/${orgId}/entity-work`, {
        method: "POST",
        body: JSON.stringify({
          orgId,
          entityUuid,
          actorUuid,
          types,
          search,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          pagination: { page, pageSize },
        }),
      }).then((res) => res.json()),
    {
      keepPreviousData: true,
      staleTime: 20_000,
    }
  );
  const dataRef = useRef(query.data);
  dataRef.current = query.data || dataRef.current;
  return {
    orgId,
    entityUuid,
    actorUuid,
    types,
    search,
    query,
    dataRef,
    setPagination,
  };
}

export const entityWorkContext = createContext<EntityWorkContext>(
  undefined as unknown as EntityWorkContext
);

export function useEntityWorkContext() {
  return useContext(entityWorkContext);
}
